/**
 * General-purpose dialog built on the native `<dialog>` element.
 *
 * Replaces MUI `Dialog` / `DialogTitle` / `DialogContent` / `DialogActions` with
 * the same native-`<dialog>` approach CoordioCore uses everywhere (DeleteDialog,
 * LocationFormPage map-dialog, CaseStatusDialog, etc.).
 *
 * Two usage modes:
 *
 * 1. **Controlled (open/onClose)** — mirrors the old MUI Dialog API.
 *    ```tsx
 *    <CortexDialog open={isOpen} onClose={() => setIsOpen(false)} title="Edit Item">
 *      <CortexDialogBody>…</CortexDialogBody>
 *      <CortexDialogFooter>…</CortexDialogFooter>
 *    </CortexDialog>
 *    ```
 *
 * 2. **Imperative (ref)** — matches CoordioCore's `showModal()` / `close()` pattern.
 *    ```tsx
 *    const ref = useRef<CortexDialogHandle>(null);
 *    <CortexDialog ref={ref} title="Edit Item">…</CortexDialog>
 *    ```
 *
 * WHY `show()` instead of `showModal()`:
 * `showModal()` promotes the `<dialog>` to the browser's "top layer" which sits
 * above ALL z-index stacking contexts. Radix tooltip/popover portals render into
 * `document.body` and cannot escape the top layer — so FormField info-tooltips,
 * CoordioDropdown panels, and any Radix-portaled content appear BEHIND the dialog.
 * Using `show()` + CSS z-index + manual backdrop keeps the dialog in the normal
 * stacking context where Radix portals can layer correctly above it.
 */

import { X } from "lucide-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  type ReactNode,
} from "react";

import "./CortexDialog.scss";

// ─── Public types ────────────────────────────────────────────────────────────

export type CortexDialogSize = "sm" | "md" | "lg" | "xl" | "full";

export interface CortexDialogProps {
  /** Dialog heading displayed in the header bar. */
  title?: ReactNode;
  /** If true, the dialog is visible (controlled mode). */
  open?: boolean;
  /** Called when the user dismisses the dialog (Escape, backdrop click, close button). */
  onClose?: () => void;
  /** Size preset — controls max-width. Defaults to "md" (520px). */
  size?: CortexDialogSize;
  /** Show the close (×) button in the header. Defaults to true. */
  showClose?: boolean;
  /** Extra className for the `<dialog>` element. */
  className?: string;
  /** `data-testid` for the `<dialog>` element. */
  "data-testid"?: string;
  children?: ReactNode;
}

export type CortexDialogHandle = {
  showModal: () => void;
  close: () => void;
};

// ─── Sub-components ──────────────────────────────────────────────────────────

/** Body area inside the dialog — applies padding and scroll. */
export function CortexDialogBody({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={`cortex-dialog__body ${className}`.trim()}>{children}</div>;
}

/** Footer / actions bar — right-aligned buttons with top border. */
export function CortexDialogFooter({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={`cortex-dialog__footer ${className}`.trim()}>{children}</div>;
}

// ─── Main component ─────────────────────────────────────────────────────────

export const CortexDialog = forwardRef<CortexDialogHandle, CortexDialogProps>(
  function CortexDialog(
    {
      title,
      open,
      onClose,
      size = "md",
      showClose = true,
      className = "",
      "data-testid": testId,
      children,
    },
    ref,
  ) {
    const dialogRef = useRef<HTMLDialogElement>(null);

    // ── Imperative handle ────────────────────────────────────────────
    useImperativeHandle(ref, () => ({
      showModal() {
        const el = dialogRef.current;
        if (!el) return;
        el.show();
        el.classList.add("cortex-dialog--open");
      },
      close() {
        const el = dialogRef.current;
        if (!el) return;
        el.classList.remove("cortex-dialog--open");
        el.close();
      },
    }));

    // ── Controlled mode — sync `open` prop → native dialog ──────────
    useEffect(() => {
      const el = dialogRef.current;
      if (!el) return;

      if (open && !el.open) {
        el.show();
        el.classList.add("cortex-dialog--open");
      } else if (!open && el.open) {
        el.classList.remove("cortex-dialog--open");
        el.close();
      }
    }, [open]);

    // ── Escape key handler (show() doesn't auto-close on Escape) ─────
    useEffect(() => {
      const el = dialogRef.current;
      if (!el || !open) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          e.preventDefault();
          onClose?.();
        }
      };
      el.addEventListener("keydown", handleKeyDown);
      return () => el.removeEventListener("keydown", handleKeyDown);
    }, [open, onClose]);

    // ── Close handler ────────────────────────────────────────────────
    const handleClose = useCallback(() => {
      onClose?.();
    }, [onClose]);

    const handleBackdropClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      },
      [handleClose],
    );

    const sizeClass = size ? `cortex-dialog--${size}` : "";

    return (
      <>
        {/* Manual backdrop — show() doesn't create ::backdrop */}
        {open && (
          <div
            className="cortex-dialog-backdrop"
            onClick={handleClose}
            aria-hidden
          />
        )}
        <dialog
          ref={dialogRef}
          className={`cortex-dialog ${sizeClass} ${className}`.trim()}
          onClose={handleClose}
          data-testid={testId}
        >
          <div
            className="cortex-dialog__panel"
            onClick={handleBackdropClick}
          >
            <div
              className="cortex-dialog__panel-inner"
              onClick={(e) => e.stopPropagation()}
            >
              {/* ── Header ──────────────────────────────────────────── */}
              {(title || showClose) && (
                <div className="cortex-dialog__header">
                  {title ? (
                    <h2 className="cortex-dialog__title">{title}</h2>
                  ) : (
                    <span />
                  )}
                  {showClose && (
                    <button
                      type="button"
                      className="cortex-dialog__close"
                      onClick={handleClose}
                      aria-label="Close"
                      data-testid={testId ? `${testId}-close` : undefined}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              )}

              {/* ── Content (body + footer provided by caller) ──────── */}
              {children}
            </div>
          </div>
        </dialog>
      </>
    );
  },
);
